import { Link } from "react-router-dom";
import { Check, ExternalLink, Star, Camera, MapPin, Phone, Clock, MessageSquareQuote, Search, ArrowRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CtaBand from "@/components/site/CtaBand";
import SEO from "@/components/site/SEO";
import { COMPANY } from "@/lib/siteData";

const STEPS = [
  {
    num: "01",
    title: "Créer ou revendiquer la fiche",
    text: "Rendez-vous sur google.com/business et connectez-vous avec un compte Google. Recherchez « RM Plomberie Chauffage VMC ». Si la fiche existe déjà, cliquez sur « Revendiquer cette entreprise ». Sinon, créez-la en renseignant le nom exact.",
    link: "https://www.google.com/business/",
  },
  {
    num: "02",
    title: "Renseigner les informations exactes",
    text: "Saisir les informations identiques à celles du site (cohérence NAP) : nom commercial, téléphone, adresse, site web. Toute différence pénalise le référencement local.",
  },
  {
    num: "03",
    title: "Choisir la bonne catégorie",
    text: "Catégorie principale : Plombier. Catégories secondaires : Chauffagiste, Service de réparation de chaudières, Service de ventilation.",
  },
  {
    num: "04",
    title: "Vérifier la fiche",
    text: "Google envoie un code par carte postale, SMS ou téléphone à l'adresse renseignée. La vérification est obligatoire pour apparaître dans Google Maps et les résultats locaux.",
  },
  {
    num: "05",
    title: "Optimiser pour le SEO local",
    text: "Ajouter la description optimisée, les horaires, les zones d'intervention, les services, la galerie photos avant/après, le logo et la photo de couverture.",
  },
  {
    num: "06",
    title: "Publier et collecter des avis",
    text: "Publier régulièrement des actualités (photos chantiers, nouveautés). Demander systématiquement un avis Google à chaque client satisfait — c'est le facteur SEO local n°1.",
  },
];

const CHECKLIST = [
  "Description professionnelle optimisée SEO",
  "Ajout des horaires (7j/7 - 24h/24)",
  "Ajout du site internet (rmplomberiechauffagevmc.com)",
  "Ajout détaillé des services (plomberie, chauffage, VMC)",
  "Ajout des zones d'intervention (Beblenheim, Colmar, Mulhouse, Strasbourg)",
  "Galerie photos avant / après chantiers",
  "Logo officiel RM Plomberie",
  "Photo de couverture professionnelle",
  "Publication Google régulière (actualités, posts)",
  "Demande automatique d'avis clients",
  "Intégration du lien Google Maps sur le site",
  "Boutons \"Itinéraire\" et \"Appeler\" actifs",
];

const SEO_LOCAL = [
  "Cohérence Nom / Adresse / Téléphone (NAP)",
  "Balisage Schema.org LocalBusiness / Plumber",
  "Coordonnées identiques entre site et fiche Google",
  "Optimisation pour Beblenheim",
  "Optimisation pour Colmar",
  "Optimisation pour Mulhouse",
  "Optimisation pour Strasbourg",
  "Optimisation Haut-Rhin (68) et Grand Est",
];

const SUGGESTED_DESCRIPTION = `RM Plomberie Chauffage VMC — Artisan plombier chauffagiste basé à Beblenheim (68980), intervenant à Colmar, Mulhouse, Strasbourg et dans tout le Haut-Rhin. Dirigée par Ramzi Latrach, notre entreprise vous accompagne pour l'installation, le dépannage et l'entretien de vos équipements : plomberie complète, chaudière, radiateurs, pompe à chaleur, plancher chauffant, VMC simple et double flux, ballon d'eau chaude, salle de bain, débouchage de canalisations. Disponibilité 7j/7, déplacement gratuit, devis transparents, travail soigné. Contactez-nous au 07 67 62 44 36.`;

export default function GoogleBusiness() {
  return (
    <div data-testid="gbp-page">
      <SEO
        title="Google Business Profile - Guide de création | RM Plomberie"
        description="Guide complet pour créer et optimiser la fiche Google Business Profile de RM Plomberie Chauffage VMC : étapes, infos NAP, checklist SEO local."
        keywords="Google Business Profile, fiche Google plombier, SEO local Colmar, référencement plombier Beblenheim"
      />
      <PageHero
        breadcrumb="Google Business"
        kicker="Référencement local"
        title=" Business Profile"
        highlight="Google"
        subtitle="Tout ce qu'il faut pour créer, optimiser et faire vivre votre fiche Google. Une fiche bien optimisée = des appels qualifiés et un référencement local maximum."
        useLogo
      />

      {/* Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Mode d'emploi</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#001B35]">
              Créer et optimiser votre fiche Google en <span className="text-[#005BBB]">6 étapes</span>
            </h2>
            <div className="mt-4 w-16 h-1 bg-[#005BBB] mx-auto rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {STEPS.map((s) => (
              <article key={s.num} data-testid={`gbp-step-${s.num}`} className="rm-card bg-white rounded-2xl border border-gray-100 p-7 shadow-[0_4px_20px_-12px_rgba(0,27,53,0.15)]">
                <span className="font-display text-5xl font-bold text-[#005BBB]/15">{s.num}</span>
                <h3 className="font-display text-lg font-bold text-[#001B35] mt-2 uppercase tracking-wider">{s.title}</h3>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{s.text}</p>
                {s.link && (
                  <a href={s.link} target="_blank" rel="noopener noreferrer" data-testid={`gbp-step-link-${s.num}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#005BBB] hover:text-[#001B35] uppercase tracking-wider">
                    Accéder à Google Business <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Infos officielles */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Informations officielles à saisir</p>
            <h2 className="font-display text-3xl font-bold text-[#001B35]">Les informations exactes (NAP)</h2>
            <div className="mt-4 w-12 h-1 bg-[#005BBB] rounded-full" />
            <div className="mt-7 bg-white rounded-2xl p-7 shadow-sm space-y-4">
              <Row label="Nom" value={COMPANY.name} />
              <Row label="Catégorie principale" value="Plombier" />
              <Row label="Catégories secondaires" value="Chauffagiste · Service de réparation de chaudières · Service de ventilation" />
              <Row label="Téléphone" value={COMPANY.phone} />
              <Row label="E-mail" value={COMPANY.email} />
              <Row label="Adresse" value={COMPANY.address} />
              <Row label="Site web" value={`https://${COMPANY.domain}`} />
              <Row label="SIRET" value={COMPANY.siret} />
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Description suggérée</p>
            <h2 className="font-display text-3xl font-bold text-[#001B35]">Texte à coller dans la fiche</h2>
            <div className="mt-4 w-12 h-1 bg-[#005BBB] rounded-full" />
            <div className="mt-7 bg-[#001B35] text-white rounded-2xl p-7 shadow-sm dark-grain">
              <MessageSquareQuote className="w-7 h-7 text-[#5dade2] mb-3" />
              <p className="text-sm leading-relaxed text-[#cfdbe8] whitespace-pre-line">{SUGGESTED_DESCRIPTION}</p>
              <p className="mt-5 text-xs uppercase tracking-wider text-[#9bb6d6]">
                Optimisée pour : plombier Beblenheim · chauffagiste Colmar · VMC Haut-Rhin
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Checklist d'optimisation</p>
            <h2 className="font-display text-3xl font-bold text-[#001B35]">À faire pour briller sur Google</h2>
            <div className="mt-4 w-12 h-1 bg-[#005BBB] rounded-full" />
            <ul className="mt-7 space-y-3">
              {CHECKLIST.map((c) => (
                <li key={c} className="flex items-start gap-3 bg-[#F5F7FA] rounded-xl px-4 py-3">
                  <Check className="w-5 h-5 mt-0.5 text-[#005BBB] flex-shrink-0" />
                  <span className="text-sm text-[#001B35]">{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Critères SEO local</p>
            <h2 className="font-display text-3xl font-bold text-[#001B35]">Pour un référencement maximum</h2>
            <div className="mt-4 w-12 h-1 bg-[#005BBB] rounded-full" />
            <ul className="mt-7 space-y-3">
              {SEO_LOCAL.map((c) => (
                <li key={c} className="flex items-start gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
                  <Search className="w-5 h-5 mt-0.5 text-[#D90000] flex-shrink-0" />
                  <span className="text-sm text-[#001B35]">{c}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 grid grid-cols-3 gap-3 text-center">
              {[{ Icon: Star, label: "Avis clients" }, { Icon: Camera, label: "Photos" }, { Icon: MapPin, label: "Maps" }].map(({ Icon, label }) => (
                <div key={label} className="bg-[#001B35] text-white rounded-2xl p-5">
                  <Icon className="w-6 h-6 mx-auto text-[#5dade2] mb-2" />
                  <p className="text-xs uppercase tracking-wider">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
      <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#005BBB] sm:w-44 flex-shrink-0">{label}</span>
      <span className="text-sm text-[#001B35] flex-1 break-words">{value}</span>
    </div>
  );
}
